import { Flame, Sparkles, Bookmark, Eye, Radio, Crown, Hourglass } from 'lucide-react';

/**
 * StatusBadge
 * -----------
 * Small editorial chip rendered on story / athlete cards. Derives from the
 * story document so we don't have to manually flag everything in the CMS:
 *
 *   • LIVE       — story.is_live === true                (manual flag)
 *   • EXCLUSIVE  — story.channel === 'flyt'              (FLYT-only content)
 *   • COMING SOON — story.coming_soon === true
 *   • BREAKING   — story.breaking === true
 *   • TRENDING   — story.view_count >= TRENDING_THRESHOLD
 *   • NEW        — story.publish_date within the last 7d
 *   • FEATURED   — story.featured === true               (fallback)
 *
 * Only the FIRST matching badge is shown — priority order above.
 */
const TRENDING_THRESHOLD = 1500;
const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const STYLE = {
  live:     { label: 'LIVE',        Icon: Radio,    cls: 'bg-red-500 text-white ring-1 ring-red-300/40' },
  exclusive:{ label: 'EXCLUSIVE',   Icon: Crown,    cls: 'bg-amber-300 text-black ring-1 ring-amber-100/60' },
  coming:   { label: 'COMING SOON', Icon: Hourglass,cls: 'bg-white text-black ring-1 ring-blue-300/40' },
  breaking: { label: 'BREAKING',    Icon: Flame,    cls: 'bg-red-500 text-white' },
  trending: { label: 'TRENDING',    Icon: Flame,    cls: 'bg-orange-500 text-white' },
  new:      { label: 'NEW',         Icon: Sparkles, cls: 'bg-blue-500 text-white' },
  featured: { label: 'FEATURED',    Icon: Bookmark, cls: 'bg-black text-white ring-1 ring-white/15' },
  watched:  { label: 'MOST WATCHED',Icon: Eye,      cls: 'bg-white text-black ring-1 ring-black/10' },
};

export const deriveBadge = (story, forceKind = null) => {
  if (!story) return null;
  if (forceKind && STYLE[forceKind]) return forceKind;
  if (story.is_live) return 'live';
  if (story.channel === 'flyt') return 'exclusive';
  if (story.coming_soon) return 'coming';
  if (story.breaking) return 'breaking';
  if ((story.view_count || 0) >= TRENDING_THRESHOLD) return 'trending';
  if (story.publish_date) {
    const t = new Date(story.publish_date).getTime();
    if (Number.isFinite(t) && Date.now() - t <= NEW_WINDOW_MS) return 'new';
  }
  if (story.featured) return 'featured';
  return null;
};

const StatusBadge = ({ story, kind, className = '', size = 'sm' }) => {
  const resolved = deriveBadge(story, kind);
  if (!resolved) return null;
  const s = STYLE[resolved];
  if (!s) return null;
  const sizeCls = size === 'lg' ? 'px-3 py-1 text-[11px]' : 'px-2 py-0.5 text-[10px]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-widest ${sizeCls} ${s.cls} ${className}`}
      data-testid={`status-badge-${resolved}`}
    >
      <s.Icon className="w-3 h-3" strokeWidth={2.5} />
      {s.label}
    </span>
  );
};

export default StatusBadge;
